import React, { useState, lazy } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { LOGO_URL, MAPS_URL, INSTAGRAM_URL } from '../data/constants';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { Instagram, MessageCircle, MapPin, Heart, ChevronDown } from 'lucide-react';
import { LegalModal } from './LegalModal';
type LegalKey = 'cookies' | 'privacy' | 'terms' | 'dataProtection' | null;
type AccordionKey = 'links' | 'contact' | 'legal' | null;
export const Footer = () => {
  const {
    t
  } = useLanguage();
  const [openLegal, setOpenLegal] = useState<LegalKey>(null);
  const [openAccordion, setOpenAccordion] = useState<AccordionKey>(null);
  const handleWhatsApp = () => {
    const message = t('wa.help');
    window.open(getWhatsAppUrl(message), '_blank');
  };
  const legalLinks: {
    key: LegalKey;
    labelKey: string;
  }[] = [{
    key: 'cookies',
    labelKey: 'footer.cookies'
  }, {
    key: 'privacy',
    labelKey: 'footer.privacy'
  }, {
    key: 'terms',
    labelKey: 'footer.terms'
  }, {
    key: 'dataProtection',
    labelKey: 'footer.dataProtection'
  }];
  const toggle = (key: Exclude<AccordionKey, null>) => setOpenAccordion(openAccordion === key ? null : key);
  return <>
      <footer className="bg-[#001040] text-white/80 pt-10 sm:pt-14 md:pt-16 pb-6 sm:pb-8 border-t border-white/10" data-id="element-182">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-183">
          {/* Brand block — only on mobile, centered */}
          <div className="md:hidden text-center mb-8" data-id="element-184">
            <img src={LOGO_URL} alt="Kevin Boats" loading="lazy" decoding="async" className="h-24 object-contain mb-3 mx-auto" data-id="element-185" />
            <p className="text-sm" data-id="element-186">Torrevieja · España</p>
            <p className="text-xs text-white/60 mt-2 max-w-xs mx-auto" data-id="element-187">
              {t('footer.desc')}
            </p>
          </div>

          {/* Mobile: stacked accordions */}
          <div className="md:hidden mt-8 space-y-2 border-t border-white/10 pt-2" data-id="element-188">
            {/* Links */}
            <div className="border-b border-white/10" data-id="element-189">
              <button onClick={() => toggle('links')} className="w-full flex justify-between items-center py-4 text-white font-bold uppercase tracking-wider text-xs" data-id="element-190">
                <span data-id="element-191">{t('footer.links')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === 'links' ? 'rotate-180' : ''}`} data-id="element-192" />
              </button>
              {openAccordion === 'links' && <ul className="pb-4 space-y-3 text-sm" data-id="element-193">
                  <li data-id="element-194">
                    <a href="#barcos" className="hover:text-turquoise" data-id="element-195">
                      {t('fleet.filter.noLicense')}
                    </a>
                  </li>
                  <li data-id="element-196">
                    <a href="#barcos" className="hover:text-turquoise" data-id="element-197">
                      {t('fleet.filter.license')}
                    </a>
                  </li>
                  <li data-id="element-198">
                    <a href="#barcos" className="hover:text-turquoise" data-id="element-199">
                      {t('fleet.filter.premium')}
                    </a>
                  </li>
                  <li data-id="element-200">
                    <a href="#motos" className="hover:text-turquoise" data-id="element-201">
                      {t('nav.jetski')}
                    </a>
                  </li>
                  <li data-id="element-202">
                    <a href="#licencias" className="hover:text-turquoise" data-id="element-203">
                      {t('nav.licenses')}
                    </a>
                  </li>
                </ul>}
            </div>

            {/* Contact */}
            <div className="border-b border-white/10" data-id="element-204">
              <button onClick={() => toggle('contact')} className="w-full flex justify-between items-center py-4 text-white font-bold uppercase tracking-wider text-xs" data-id="element-205">
                <span data-id="element-206">{t('footer.contact')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === 'contact' ? 'rotate-180' : ''}`} data-id="element-207" />
              </button>
              {openAccordion === 'contact' && <ul className="pb-4 space-y-4 text-sm" data-id="element-208">
                  <li data-id="element-209">
                    <button onClick={handleWhatsApp} className="flex items-center space-x-3 hover:text-turquoise" data-id="element-210">
                      <MessageCircle className="w-4 h-4 flex-shrink-0" data-id="element-211" />
                      <span data-id="element-212">+34 692 489 818</span>
                    </button>
                  </li>
                  <li data-id="element-213">
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-214">
                      <Instagram className="w-4 h-4 flex-shrink-0" data-id="element-215" />
                      <span data-id="element-216">@kevin.boats</span>
                    </a>
                  </li>
                  <li data-id="element-217">
                    <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-218">
                      <MapPin className="w-4 h-4 flex-shrink-0" data-id="element-219" />
                      <span data-id="element-220">Torrevieja, España</span>
                    </a>
                  </li>
                </ul>}
            </div>

            {/* Legal */}
            <div className="border-b border-white/10" data-id="element-221">
              <button onClick={() => toggle('legal')} className="w-full flex justify-between items-center py-4 text-white font-bold uppercase tracking-wider text-xs" data-id="element-222">
                <span data-id="element-223">{t('footer.legal')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === 'legal' ? 'rotate-180' : ''}`} data-id="element-224" />
              </button>
              {openAccordion === 'legal' && <ul className="pb-4 space-y-3 text-sm" data-id="element-225">
                  {legalLinks.map(link => <li key={link.key} data-id="element-226">
                      <button onClick={() => setOpenLegal(link.key)} className="hover:text-turquoise text-left" data-id="element-227">
                        {t(link.labelKey)}
                      </button>
                    </li>)}
                </ul>}
            </div>
          </div>

          {/* Desktop: 4-column grid */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-228">
            {/* Brand */}
            <div className="space-y-4" data-id="element-229">
              <img src={LOGO_URL} alt="Kevin Boats" loading="lazy" decoding="async" className="h-28 object-contain mb-4" data-id="element-230" />
              <p className="text-sm" data-id="element-231">Torrevieja · España</p>
              <p className="text-sm" data-id="element-232">{t('footer.desc')}</p>
            </div>

            <div data-id="element-233">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm" data-id="element-234">
                {t('footer.links')}
              </h4>
              <ul className="space-y-3 text-sm" data-id="element-235">
                <li data-id="element-236">
                  <a href="#barcos" className="hover:text-turquoise" data-id="element-237">
                    {t('fleet.filter.noLicense')}
                  </a>
                </li>
                <li data-id="element-238">
                  <a href="#barcos" className="hover:text-turquoise" data-id="element-239">
                    {t('fleet.filter.license')}
                  </a>
                </li>
                <li data-id="element-240">
                  <a href="#barcos" className="hover:text-turquoise" data-id="element-241">
                    {t('fleet.filter.premium')}
                  </a>
                </li>
                <li data-id="element-242">
                  <a href="#motos" className="hover:text-turquoise" data-id="element-243">
                    {t('nav.jetski')}
                  </a>
                </li>
                <li data-id="element-244">
                  <a href="#licencias" className="hover:text-turquoise" data-id="element-245">
                    {t('nav.licenses')}
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-246">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm" data-id="element-247">
                {t('footer.contact')}
              </h4>
              <ul className="space-y-4 text-sm" data-id="element-248">
                <li data-id="element-249">
                  <button onClick={handleWhatsApp} className="flex items-center space-x-3 hover:text-turquoise text-left" data-id="element-250">
                    <MessageCircle className="w-5 h-5" data-id="element-251" />
                    <span data-id="element-252">+34 692 489 818</span>
                  </button>
                </li>
                <li data-id="element-253">
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-254">
                    <Instagram className="w-5 h-5" data-id="element-255" />
                    <span data-id="element-256">@kevin.boats</span>
                  </a>
                </li>
                <li data-id="element-257">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-258">
                    <MapPin className="w-5 h-5" data-id="element-259" />
                    <span data-id="element-260">Torrevieja, España</span>
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-261">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm" data-id="element-262">
                {t('footer.legal')}
              </h4>
              <ul className="space-y-3 text-sm" data-id="element-263">
                {legalLinks.map(link => <li key={link.key} data-id="element-264">
                    <button onClick={() => setOpenLegal(link.key)} className="hover:text-turquoise text-left" data-id="element-265">
                      {t(link.labelKey)}
                    </button>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 sm:pt-8 mt-6 md:mt-0 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4 text-[11px] sm:text-xs text-white/50 text-center md:text-left" data-id="element-266">
            <p className="leading-relaxed" data-id="element-267">
              © {new Date().getFullYear()} Kevin Boats · Torrevieja · España
              <br className="sm:hidden" data-id="element-268" />
              <span className="hidden sm:inline" data-id="element-269"> · </span>
              {t('footer.rights')}
            </p>
            <p className="flex items-center space-x-1.5" data-id="element-270">
              <span data-id="element-271">{t('footer.credit')}</span>
              <Heart className="w-3 h-3 text-turquoise fill-turquoise" data-id="element-272" />
              <a href="#" className="font-semibold text-white/70 hover:text-turquoise transition-colors" data-id="element-273">
                KineticGrowthStudio
              </a>
            </p>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={openLegal === 'cookies'} onClose={() => setOpenLegal(null)} titleKey="legal.cookies.title" bodyKey="legal.cookies.body" data-id="element-274" />
      <LegalModal isOpen={openLegal === 'privacy'} onClose={() => setOpenLegal(null)} titleKey="legal.privacy.title" bodyKey="legal.privacy.body" data-id="element-275" />
      <LegalModal isOpen={openLegal === 'terms'} onClose={() => setOpenLegal(null)} titleKey="legal.terms.title" bodyKey="legal.terms.body" data-id="element-276" />
      <LegalModal isOpen={openLegal === 'dataProtection'} onClose={() => setOpenLegal(null)} titleKey="legal.dataProtection.title" bodyKey="legal.dataProtection.body" data-id="element-277" />
    </>;
};